import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { StepUpCancelledError, StepUpContext } from './stepUpContext'

/**
 * Transaction-PIN step-up, resolved centrally rather than per page.
 *
 * The backend guard fails closed and gates every money action across all
 * verticals (DEF-29). Rather than bolt a PIN field onto each of the ~18 surfaces
 * that submit one, `useActionSubmit` catches the `PIN_REQUIRED` rejection, asks
 * here for a PIN, and retries the *same* request — same `action_id`, so the retry
 * is idempotent by CLAUDE.md Rule 1.
 *
 * Pages that already collect a PIN inline (checkout, order cancel) pass it up
 * front and never reach this prompt.
 */

interface PinPromptRequest {
  reason: 'required' | 'invalid'
  resolve: (pin: string) => void
  reject: (error: Error) => void
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PinPromptRequest | null>(null)
  const [pin, setPin] = useState('')
  const pendingRef = useRef<PinPromptRequest | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const requestPin = useCallback((reason: 'required' | 'invalid' = 'required') => {
    return new Promise<string>((resolve, reject) => {
      const request: PinPromptRequest = { reason, resolve, reject }
      pendingRef.current = request
      setPin('')
      setPending(request)
    })
  }, [])

  // Move focus into the prompt when it opens — the action is blocked on this
  // input, so leaving focus behind on the page would strand keyboard users.
  useEffect(() => {
    if (pending) inputRef.current?.focus()
  }, [pending])

  const close = useCallback(() => {
    pendingRef.current = null
    setPending(null)
    setPin('')
  }, [])

  const confirm = useCallback(() => {
    const request = pendingRef.current
    if (!request) return
    const entered = pin.trim()
    if (!/^\d{4,6}$/.test(entered)) return
    close()
    request.resolve(entered)
  }, [pin, close])

  const cancel = useCallback(() => {
    const request = pendingRef.current
    close()
    request?.reject(new StepUpCancelledError())
  }, [close])

  const value = useMemo(() => ({ requestPin }), [requestPin])

  return (
    <StepUpContext.Provider value={value}>
      {children}
      {pending && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="step-up-title"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <h2 id="step-up-title" className="text-lg font-semibold text-stone-900">
              Confirm with your PIN
            </h2>
            <p className="mt-1 text-sm text-stone-600">
              {pending.reason === 'invalid'
                ? 'That PIN was not correct. Please try again.'
                : 'This action moves money, so we need your transaction PIN.'}
            </p>
            <label htmlFor="step-up-pin" className="mt-4 block text-sm font-medium text-stone-700">
              Transaction PIN
            </label>
            <input
              id="step-up-pin"
              ref={inputRef}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirm()
                if (e.key === 'Escape') cancel()
              }}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-lg tracking-widest"
              placeholder="••••"
            />
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={cancel}
                className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={!/^\d{4,6}$/.test(pin.trim())}
                className="flex-1 rounded-lg bg-stone-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </StepUpContext.Provider>
  )
}
